// Renders an exploration's JOURNEY-AUTHORING DATA: the wad the exploration guidance produces recording
// how each journey came to be written (explorations.journey_authoring).
//
// The wad is GUIDANCE-OWNED. This component reads it defensively — every field optional, every list
// tolerated missing — and falls back to a raw JSON dump for anything it does not recognize, so the
// guidance can change the shape without the app needing a matching deploy. Nothing here is a contract;
// the backend stores the value verbatim and has no opinion about it at all.

import { useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight, Plus, Copy, XCircle, AlertTriangle, Cpu } from 'lucide-react'
import './JourneyAuthoringData.css'

interface Decision {
  seq?: number
  mode?: string
  llmJobId?: string | null
  existingJourneys?: Array<{ id?: string | null; name?: string; description?: string | null }>
  uncoveredBefore?: Array<{ page?: string; label?: string; text?: string; instances?: number }>
  proposed?: { name?: string; description?: string; covers?: string[] } | null
  outcome?: string
  journeyId?: string | null
  labelsUsed?: string[]
  detail?: string | null
}

interface Wad { decisions?: Decision[]; controlsTotal?: number; controlsCovered?: number }

const OUTCOME_ICON: Record<string, ReactNode> = {
  created: <Plus size={11} aria-hidden />,
  superseded: <Copy size={11} aria-hidden />,
  duplicate: <Copy size={11} aria-hidden />,
  rejected: <XCircle size={11} aria-hidden />,
  error: <AlertTriangle size={11} aria-hidden />,
}

// Controls a journey SAID it would exercise but whose steps never touched — the divergence worth
// showing, since a claim that is never delivered still retires the control from the gap list.
function ghostClaims(d: Decision): string[] {
  const claimed = d.proposed?.covers ?? []
  const used = new Set(d.labelsUsed ?? [])
  return claimed.filter((c) => !used.has(c))
}

function DecisionRow({ d, index }: { d: Decision; index: number }) {
  const [open, setOpen] = useState(false)
  const outcome = d.outcome ?? 'pending'
  const claimed = d.proposed?.covers ?? []
  const used = d.labelsUsed ?? []
  const ghosts = ghostClaims(d)
  return (
    <div className="jad__row" data-testid={`journey-decision-${index}`}>
      <button
        type="button"
        className="jad__row-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-analytics-event="exploration-journey-decision-toggle"
      >
        {open ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
        <span className="jad__seq">#{d.seq ?? index + 1}</span>
        {d.mode && <span className="jad__badge jad__badge--mode">{d.mode}</span>}
        <span className="jad__name">{d.proposed?.name ?? <em>declined to propose a journey</em>}</span>
        <span className={`jad__badge jad__badge--${outcome}`}>{OUTCOME_ICON[outcome]} {outcome}</span>
        {(claimed.length > 0 || used.length > 0) && (
          <span className="jad__counts" title={claimed.length > 0 ? 'labels claimed → labels the authored steps used' : 'controls the authored steps exercise'}>
            {claimed.length > 0 ? `${claimed.length} → ${used.length}` : `${used.length} control${used.length === 1 ? '' : 's'}`}
            {ghosts.length > 0 && <span className="jad__ghost-count"> ({ghosts.length} unmet)</span>}
          </span>
        )}
      </button>
      {open && (
        <div className="jad__row-body">
          {d.proposed?.description && <p className="jad__desc">{d.proposed.description}</p>}
          {d.detail && <p className="jad__detail">{d.detail}</p>}

          {(claimed.length > 0 || used.length > 0) && (
            <div className="jad__block">
              <h4>Controls</h4>
              {/* Older runs asked the planner to predict which controls it would exercise; newer ones do
                  not, so this block is absent for them and only the authored labels are shown. */}
              {claimed.length > 0 && (
                <div className="jad__labels">
                  <span className="jad__labels-title">claimed</span>
                  {claimed.map((l) => (
                    <span key={`c-${l}`} className={`jad__label${used.includes(l) ? '' : ' jad__label--ghost'}`}>{l}</span>
                  ))}
                </div>
              )}
              <div className="jad__labels">
                <span className="jad__labels-title">{claimed.length > 0 ? 'used' : 'controls'}</span>
                {used.length === 0 ? <em>none</em> : used.map((l, i) => (
                  <span key={`u-${l}-${i}`} className={`jad__label${claimed.includes(l) ? '' : ' jad__label--extra'}`}>{l}</span>
                ))}
              </div>
              {ghosts.length > 0 && (
                <p className="jad__warn">
                  <AlertTriangle size={11} aria-hidden /> Claimed but never exercised: {ghosts.join(', ')}
                </p>
              )}
            </div>
          )}

          {!!d.uncoveredBefore?.length && (
            <div className="jad__block">
              <h4>Unclaimed controls it was shown ({d.uncoveredBefore.length})</h4>
              <ul className="jad__uncovered">
                {d.uncoveredBefore.map((c, i) => (
                  <li key={`${c.label}-${i}`}>
                    <span className="jad__page">{c.page || '/'}</span>
                    <span className={`jad__label${claimed.includes(c.label ?? '') ? ' jad__label--picked' : ''}`}>{c.label}</span>
                    <span className="jad__ctext">{c.text}</span>
                    {!!c.instances && c.instances > 1 && <span className="jad__times">×{c.instances}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!!d.existingJourneys?.length && (
            <div className="jad__block">
              <h4>Journeys already in front of the planner ({d.existingJourneys.length})</h4>
              <ul className="jad__existing">
                {d.existingJourneys.map((j, i) => <li key={`${j.id ?? j.name}-${i}`}>{j.name}</li>)}
              </ul>
            </div>
          )}

          {d.llmJobId && (
            <p className="jad__job">
              <Cpu size={11} aria-hidden /> Plan call: <code>{d.llmJobId}</code>
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default function JourneyAuthoringData({ data }: { data: unknown }) {
  if (!data || typeof data !== 'object') return null
  const wad = data as Wad
  const decisions = Array.isArray(wad.decisions) ? wad.decisions : null
  // Unrecognized shape — show it rather than hide it. The guidance owns this structure and may change
  // it without the app; a silent blank would look like "the run recorded nothing".
  if (!decisions) {
    return (
      <section className="exploration-detail__section" data-testid="journey-authoring-raw">
        <h2 className="exploration-detail__section-title">Journey Authoring</h2>
        <pre className="jad__raw">{JSON.stringify(data, null, 2)}</pre>
      </section>
    )
  }
  const total = wad.controlsTotal
  const covered = wad.controlsCovered
  return (
    <section className="exploration-detail__section" data-testid="journey-authoring">
      <h2 className="exploration-detail__section-title">
        Journey Authoring ({decisions.length} {decisions.length === 1 ? 'decision' : 'decisions'})
        {typeof total === 'number' && typeof covered === 'number' && total > 0 && (
          <span className="jad__coverage"> — {covered}/{total} controls covered</span>
        )}
      </h2>
      <div className="jad__rows">
        {decisions.map((d, i) => <DecisionRow key={d.seq ?? i} d={d} index={i} />)}
      </div>
    </section>
  )
}
